import React, { useState } from 'react';
import { Select, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useProjectsForSelection } from '../../hooks/useProject';
import { QuickProjectCreationModal } from './QuickProjectCreationModal';

export interface Project {
  id: string;
  project_number: string;
  project_name: string;
  customer_id?: string;
  customer_name: string;
  sales_order_id?: string;
  project_status?: string;
}

interface ProjectSelectorProps {
  value?: string;
  onChange: (projectId: string | undefined, projectData?: Project) => void;
  customerId?: string;
  customerName?: string;
  disabled?: boolean;
  placeholder?: string;
  /** When true, user can clear the selection (optional project). */
  allowClear?: boolean;
}

function projectSearchLabel(project: Project): string {
  return `${project.project_number} - ${project.project_name}${
    project.customer_name ? ` ${project.customer_name}` : ''
  }`;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  value,
  onChange,
  customerId,
  customerName,
  disabled,
  placeholder = 'Select or create project',
  allowClear = false,
}) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: projects, isLoading } = useProjectsForSelection();

  const filteredProjects = (projects || []).filter((project) => {
    if (!customerId && !customerName) return true;
    if (customerId && project.customer_id) {
      return project.customer_id === customerId;
    }
    if (customerId && !project.customer_id && customerName) {
      return (project.customer_name || '').trim().toLowerCase() === customerName.trim().toLowerCase();
    }
    if (customerName) {
      return (project.customer_name || '').trim().toLowerCase() === customerName.trim().toLowerCase();
    }
    // Customer selected but project has no customer linkage — exclude
    return false;
  });

  const handleSelectChange = (val: string | undefined) => {
    if (val === '__create_new__') {
      setIsCreateModalOpen(true);
      return;
    }
    if (val == null || val === '') {
      onChange(undefined, undefined);
      return;
    }
    const project = filteredProjects.find((p) => p.id === val) || projects?.find((p) => p.id === val);
    onChange(val, project);
  };

  const handleProjectCreated = (newProject: Project) => {
    onChange(newProject.id, newProject);
    setIsCreateModalOpen(false);
  };

  return (
    <>
      <Select
        value={value}
        onChange={handleSelectChange}
        placeholder={placeholder}
        loading={isLoading}
        showSearch
        disabled={disabled}
        allowClear={allowClear}
        style={{ width: '100%' }}
        popupMatchSelectWidth={false}
        dropdownStyle={{ minWidth: 420 }}
        optionFilterProp="label"
        filterOption={(input, option) =>
          String(option?.label ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        optionRender={(option) => {
          if (option.value === '__create_new__') {
            return (
              <span>
                <PlusOutlined style={{ marginRight: 8 }} />
                Create New Project
              </span>
            );
          }
          const project = filteredProjects.find((p) => p.id === option.value);
          if (!project) return String(option.label ?? '');
          return (
            <div style={{ whiteSpace: 'normal', lineHeight: 1.35, padding: '2px 0' }}>
              <div>
                <strong>{project.project_number}</strong> — {project.project_name}
              </div>
              {(project.customer_name || project.sales_order_id) && (
                <div style={{ fontSize: 12, color: '#888' }}>
                  {project.customer_name}
                  {project.sales_order_id && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      Has SO
                    </Tag>
                  )}
                </div>
              )}
            </div>
          );
        }}
        options={[
          {
            value: '__create_new__',
            label: 'Create New Project',
          },
          ...filteredProjects.map((project) => ({
            value: project.id,
            label: projectSearchLabel(project),
          })),
        ]}
      />

      <QuickProjectCreationModal
        visible={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onSuccess={handleProjectCreated}
        defaultCustomerId={customerId}
        defaultCustomerName={customerName}
      />
    </>
  );
};
